<script setup lang="ts">
  import { Button, ButtonSize, ButtonType, IconName, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import SerpFilterGroup from './SerpFilterGroup.vue';
  import { SERP_OPERATORS_FILTER_TEXTS } from '../config/texts';
  import { getOperatorsFilterInlineOptions } from '../lib/operatorsFilter';
  import type { SerpFilterGroupId, SerpOptionsFilterGroupView } from '../model/types';

  interface OperatorsFilterProps {
    group: SerpOptionsFilterGroupView;
  }

  const props = defineProps<OperatorsFilterProps>();

  interface OperatorsFilterEmits {
    (e: 'select', groupId: SerpFilterGroupId, key: string): void;
    (e: 'reset', groupId: SerpFilterGroupId): void;
  }

  const emit = defineEmits<OperatorsFilterEmits>();

  const pinnedKeys = new Set<string>(
    props.group.options.filter((option) => option.selected).map((option) => option.key),
  );

  const isExpanded = ref<boolean>(false);

  const inlineGroup = computed<SerpOptionsFilterGroupView>(() => ({
    ...props.group,
    options: getOperatorsFilterInlineOptions(props.group.options, pinnedKeys, isExpanded.value),
  }));

  const hasHiddenOperators = computed<boolean>(() => inlineGroup.value.options.length < props.group.options.length);

  const onSelect = (groupId: SerpFilterGroupId, key: string): void => {
    emit('select', groupId, key);
  };

  const onReset = (groupId: SerpFilterGroupId): void => {
    emit('reset', groupId);
  };

  const expand = (): void => {
    isExpanded.value = true;
  };
</script>

<template>
  <div
    class="operators-filter"
    data-testid="serp-operators-filter"
  >
    <SerpFilterGroup
      :group="inlineGroup"
      @select="onSelect"
      @reset="onReset"
    >
      <template #option-label="{ option }">
        <Typo
          :variant="TypoVariant.BodyLRegular"
          tag="span"
        >
          {{ option.label }}
          <span
            v-if="option.secondaryLabel"
            class="operators-filter__secondary-label"
            :data-testid="`serp-operators-filter-secondary-label-${option.key}`"
          >
            {{ option.secondaryLabel }}
          </span>
        </Typo>
      </template>
    </SerpFilterGroup>

    <Button
      v-if="hasHiddenOperators"
      class="operators-filter__show-all"
      :type="ButtonType.Link"
      :size="ButtonSize.L"
      :icon-right="IconName.ChevronDown"
      data-testid="serp-operators-filter-show-all"
      @click="expand"
    >
      {{ SERP_OPERATORS_FILTER_TEXTS.showAll }}
    </Button>
  </div>
</template>

<style scoped lang="scss">
  .operators-filter {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);

    &__show-all {
      align-self: flex-start;
    }

    &__secondary-label {
      color: var(--color-text-secondary);
    }
  }
</style>
